<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Knowledge of basic anatomy is essential to work in professions such as physiotherapy. In this
      section of your virtual work experience, you will learn a little about some of the most
      important areas of anatomy.
    </p>
    <h4 class="mb-5">Bones, Muscles, Joints</h4>
    <p class="mb-5">How much do you know about the bones, muscles, and joints in your body?</p>

    <div class="vx-row">
      <div class="vx-col lg:w-1/2">
        <p class="mb-5">Check out this article to learn more (click the button on the right).</p>
        <p class="mb-5">
          You have a choice of reading it or having it read to you by clicking on the loudspeaker
          icon on the top left of the page.
        </p>
        <p class="mb-5">
          There’s also a helpful slide show to get your knowledge of the skeletal system up to
          standard and a video all about the different types of joints.
        </p>
      </div>
      <div class="vx-col lg:w-1/2">
        <a
          href="https://kidshealth.org/en/teens/bones-muscles-joints.html?WT.ac=ctg#catbody-basics"
          target="_blank"
        >
          <img
            src="@/assets/images/experiences/physiotherapy/article_button.jpg"
            class="mb-5"
            alt="Read the Bones, Muscles and Joints article on KidsHealth (opens in a new tab)"
          />
          <!--          <centered-image-wrapper :image="require('@/assets/images/experiences/physiotherapy/article_button.jpg')" classes="3/3"/>-->
        </a>
      </div>
    </div>

    <p class="mb-5">
      Are you an expert on all things bone, muscle, and joint-related yet? Find out by taking the
      quizzes below!
    </p>
    <h4 class="mb-5">Task 1: Bones Quiz</h4>
    <p class="mb-5">
      There are 10 questions to answer. When you answer the final question, your results will be
      generated along with any corrections.
    </p>
    <a href="https://kidshealth.org/en/kids/ssquiz.html" target="_blank">
      <centered-image-wrapper
        alt="Take the skeletal system quiz on KidsHealth (opens in a new tab)"
        :image="require('@/assets/images/experiences/physiotherapy/quiz_button.jpg')"
        classes="3/3"
      />
    </a>
    <div class="overflow-hidden mb-5">
      <p class="float-left py-2 pr-2">What was your score out of ten?</p>
      <text-block v-model="data.question1" :editable="isEditable" style="width: 350px" />
    </div>

    <p class="mb-5">
      <i
        >Look through your corrections for any questions you got wrong. Choose up to three of these
        and write the questions and answers in the spaces below. Be sure to learn the answers
        too!</i
      >
    </p>

    <div class="overflow-hidden mb-5">
      <p class="float-left py-2 pr-2">Q1.</p>
      <text-block v-model="data.question_2_1" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-3">A1.</p>
      <text-block v-model="data.answer_2_1" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-2">Q2.</p>
      <text-block v-model="data.question_2_2" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-3">A2.</p>
      <text-block v-model="data.answer_2_2" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-2">Q3.</p>
      <text-block v-model="data.question_2_3" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-3">A3.</p>
      <text-block v-model="data.answer_2_3" :editable="isEditable" class="lg:w-7/12" />
    </div>

    <p class="mb-5">
      <i
        >Now, it’s time to take it up a notch and explore some more bones in the human body. Do this
        fun quiz three times, yes, three! Don’t worry, it’s short and fun and helps you learn the
        names of the bones as you go. You’ll be able to track your progress as you record your score
        each time you complete the quiz.</i
      >
    </p>

    <h4 class="mb-5">Skeletal System Quiz</h4>
    <p class="mb-5">
      The collection of bones in the human body is called the skeletal system. It provides structure
      to the body, and each bone has a distinct purpose. This science quiz game will help you learn
      15 of the most important bones. Some, like the rib cage, provide protection for softer body
      parts, while other bones enable mobility by supporting the muscles. From the skull to the
      tibia, you’ll have the whole body covered—get started now and use this quiz to bone up on the
      skeletal system!
    </p>
    <a href="https://online.seterra.com/en-an/vgp/3800" target="_blank">
      <centered-image-wrapper
        alt="Open the Seterra skeleton labelling game (opens in a new tab)"
        :image="require('@/assets/images/experiences/physiotherapy/bones_quiz_button.jpg')"
        classes="3/3"
      />
    </a>
    <p class="mb-5">
      How did you go? Enter your results for each of the 3 times you took the quiz below. Did you
      improve?
    </p>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-2 lg:w-2/12">First Result:</p>
      <text-block
        v-model="data.question_3_1"
        :editable="isEditable"
        style="width: 100px; float: left"
        pclass="my-2"
      />
      <p class="float-left py-2 pr-2 ml-2">%</p>
    </div>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-2 lg:w-2/12">Second Result:</p>
      <text-block
        v-model="data.question_3_2"
        :editable="isEditable"
        style="width: 100px; float: left"
        pclass="my-2"
      />
      <p class="float-left py-2 pr-2 ml-2">%</p>
    </div>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-2 lg:w-2/12">Third Result:</p>
      <text-block
        v-model="data.question_3_3"
        :editable="isEditable"
        style="width: 100px; float: left"
        pclass="my-2"
      />
      <p class="float-left py-2 pr-2 ml-2">%</p>
    </div>

    <h4 class="mb-5">Task 2: Muscles Quiz</h4>
    <p class="mb-5">
      There are 10 questions to answer. When you answer the final question, your results will be
      generated along with any corrections.
    </p>
    <a href="https://kidshealth.org/en/kids/msquiz.html?ref=search#catbody-basics" target="_blank">
      <centered-image-wrapper
        alt="Take the muscular system quiz on KidsHealth (opens in a new tab)"
        :image="require('@/assets/images/experiences/physiotherapy/muscles_quiz_button.jpg')"
        classes="3/3"
      />
    </a>
    <div class="overflow-hidden">
      <h5 class="float-left py-2 pr-2">What was your score out of ten?</h5>
      <text-block
        v-model="data.question_4"
        :editable="isEditable"
        style="width: 350px"
        pclass="my-2"
      />
    </div>
    <p class="mb-5">
      <i
        >Look through your corrections for any questions you got wrong. Choose up to three of these
        and write the questions and answers in the spaces below. Be sure to learn the answers
        too!</i
      >
    </p>
    <div class="overflow-hidden mb-5">
      <p class="float-left py-2 pr-2">Q1.</p>
      <text-block v-model="data.question_4_1" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-3">A1.</p>
      <text-block v-model="data.answer_4_1" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-2">Q2.</p>
      <text-block v-model="data.question_4_2" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-3">A2.</p>
      <text-block v-model="data.answer_4_2" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-2">Q3.</p>
      <text-block v-model="data.question_4_3" :editable="isEditable" class="lg:w-7/12" />
      <p class="float-left py-2 pr-3">A3.</p>
      <text-block v-model="data.answer_4_3" :editable="isEditable" class="lg:w-7/12" />
    </div>

    <p class="mb-5">
      <i
        >Now, it’s time to take it up a notch and explore some more muscles in the human body. Do
        this fun quiz three times, yes, three! Don’t worry, it’s short and fun and helps you learn
        the names of the muscles as you go. You’ll be able to track your progress as you record your
        score each time you complete the quiz.</i
      >
    </p>
    <p class="mb-5">
      There are over 630 muscles in the human body; this quiz focuses on the 23 largest muscles—the
      ones that account for most of your mobility and strength. The soleus connects your lower leg
      bones to your heel, but it also gives your heart some help by pumping blood back towards your
      head. Strong arms often feature massive biceps, but it’s actually the triceps that are the
      largest arm muscles.
    </p>
    <p class="mb-5">
      Learning the major muscles of the body doesn’t have to be difficult—use this anatomy quiz game
      to make it fun and easy!
    </p>

    <a href="https://online.seterra.com/en-an/vgp/3811" target="_blank">
      <centered-image-wrapper
        alt="Open the Seterra muscles labelling game (opens in a new tab)"
        :image="require('@/assets/images/experiences/physiotherapy/muscles_quiz_button_2.jpg')"
        classes="3/3"
      />
    </a>
    <p class="mb-5">
      How did you go? Enter your results for each of the 3 times you took the quiz below. Did you
      improve?
    </p>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-2 lg:w-2/12">First Result:</p>
      <text-block
        v-model="data.question_5_1"
        :editable="isEditable"
        style="width: 100px; float: left"
        pclass="my-2"
      />
      <p class="float-left py-2 pr-2 ml-2">%</p>
    </div>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-2 lg:w-2/12">Second Result:</p>
      <text-block
        v-model="data.question_5_2"
        :editable="isEditable"
        style="width: 100px; float: left"
        pclass="my-2"
      />
      <p class="float-left py-2 pr-2 ml-2">%</p>
    </div>
    <div class="overflow-hidden">
      <p class="float-left py-2 pr-2 lg:w-2/12">Third Result:</p>
      <text-block
        v-model="data.question_5_3"
        :editable="isEditable"
        style="width: 100px; float: left"
        pclass="my-2"
      />
      <p class="float-left py-2 pr-2 ml-2">%</p>
    </div>

    <h4 class="mb-5">Task 3: Joints: Quiz</h4>
    <p class="mb-5">
      Watch the video below for a reminder of what joints are and what they do in the body.
    </p>
    <a
      href="https://kidshealth.org/en/teens/how-joints-work.html?ref=search#catbody-basics"
      target="_blank"
    >
      <centered-image-wrapper
        alt="Read how joints work on KidsHealth (opens in a new tab)"
        :image="require('@/assets/images/experiences/physiotherapy/joints_video_splash.jpg')"
        classes="3/3"
      />
    </a>

    <p class="mb-5">Now, answer the 10 questions below:</p>

    <div v-for="(question, index) in questions6" :key="'q1_' + index">
      <div v-if="question.multiple_choice == true">
        <p>{{ index }}. <strong>Multiple Choice: </strong>{{ question.question }}</p>
        <radio-buttons-wrapper
          v-model="data['question6_' + index]"
          :index="'q1_' + index"
          :question="question"
          :is-editable="isEditable"
          show-question="false"
        />
      </div>
      <div v-else>
        <p>{{ question.question }}</p>
        <switch-block v-model="data['question6_' + index]" :editable="isEditable" />
      </div>
    </div>
    <button class="btn btn-secondary btn-size-medium mr-5 mb-5" @click="checkAnswers">
      Submit and Check Answers
    </button>

    <vs-popup class="holamundo" title="Correct Answers" :active.sync="popupActive">
      <div v-for="(question, index) in questions6" :key="index">
        <h5 class="mb-5">{{ question.question }}</h5>
        <p class="mb-5">
          <strong>Your answer:</strong> <span> {{ data['question6_' + index] }}</span>
        </p>
        <p class="mb-5"><strong>Correct answer:</strong> {{ question.correct }}</p>
      </div>
    </vs-popup>

    <p class="mb-5">
      [Source of Joints Quiz:
      <a
        href="https://www.niams.nih.gov/health-topics/kids/healthy-joints/questions"
        target="_blank"
        >https://www.niams.nih.gov/health-topics/kids/healthy-joints/questions</a
      >]
    </p>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import RadioButtonsWrapper from '../../components/ActivitiesBlocks/RadioButtonsWrapper';

export default {
  data() {
    return {
      videos: {},

      questions6: {
        1: {
          question: '1. All joints in the human body are movable.',
          correct: false,
          description:
            'Some joints are fixed, such as the joints between bones in your skull. But many joints are movable, like knees, wrists, ankles, and knuckles',
        },
        2: {
          question:
            '2. Cartilage protects the ends of bones in your joints and allows the bones to glide smoothly over each other.',
          correct: true,
          description:
            'Cartilage is a smooth, slippery substance that covers the ends of the bones in your joints. Healthy cartilage helps the joints move smoothly. When it wears away, the bones can grind against one another, causing pain and difficulty moving the joint.',
        },
        3: {
          question: '3. Only older people get arthritis.',
          correct: false,
          description: 'Children and young adults can get some forms of arthritis.',
        },
        4: {
          question: '4. Osteoarthritis is the most common form of arthritis.',
          correct: true,
          description:
            'Especially in older people, osteoarthritis is the most common form of arthritis. It is caused by the cartilage in a joint breaking down and wearing away.',
        },
        5: {
          question:
            '5. In rheumatoid arthritis, the immune system attacks the joints, making them painful, swollen, and stiff.',
          correct: true,
          description:
            'Rheumatoid arthritis is an autoimmune disease, in which the immune system attacks healthy tissues in the joints as if they were disease-causing germs.',
        },
        6: {
          question: '6. Children can have almost any form of arthritis that affects adults.',
          correct: true,
          description:
            'Children can develop almost all types of arthritis that affect adults, but the most common type seen in children is juvenile idiopathic arthritis.',
        },
        7: {
          question:
            '7. Your joints will heal from injuries, so there’s no need to wear protective equipment when you play sports.',
          correct: false,
          description:
            'Your joints will usually heal from injuries, but the damage may lead to arthritis later in life. So it’s important to wear protective gear when you play sports and avoid injuring your joints.',
        },
        8: {
          question:
            '8. If you avoid being overweight, you will reduce the wear and tear on your joints that could lead to arthritis later in life.',
          correct: true,
          description:
            'Extra weight can put more strain on your joints and can increase your risk for osteoarthritis later in life.',
        },
        9: {
          question: '9. You don’t need more than 30 minutes of physical activity every day.',
          correct: false,
          description:
            'You should get at least 60 minutes of exercise every day. It doesn’t have to be a whole hour all at once.',
        },
        10: {
          multiple_choice: true,
          question: 'A balanced diet is considered to be one that:',
          answers: [
            {
              val: 1,
              answer:
                'a. Emphasises fruits, vegetables, whole grains, and fat-free or low-fat dairy products like milk, cheese, and yogurt.',
              correct: false,
            },
            {
              val: 2,
              answer:
                'b. Includes protein from lean meats, poultry, seafood, beans, eggs, and nuts.',
              correct: false,
            },
            {
              val: 3,
              answer:
                'c. Is low in solid fats, saturated fats, cholesterol, salt (sodium), added sugars, and refined grains.',
              correct: false,
            },
            { val: 4, answer: 'd. All of the above', correct: true },
          ],
          correct: 'd. Is all of the above',
        },
      },
      popupActive: false,
    };
  },
  methods: {
    ...methods,
    checkAnswers() {
      this.popupActive = true;
    },
  },
  computed: {
    ...computed,
  },
  inject: inject,
  components: { RadioButtonsWrapper },
};
</script>
